<template>
  <modal
    v-if="active"
    width="620px"
    :title="t('change workspace')"
    @close="close"
    :read-article-url="`https://docs.bemob.com/${locale}/moving-elements-between-workspaces`"
  >
    <template #default>
      <div class="form-container">
        <ui-select
          v-model="selectedWorkspace"
          :items="workspacesList"
          :label="t('Select the workspace to which you wish to move the element')"
          :return-object="true"
          item-text="name"
          :error-messages="errorsDict['selectedWorkspace']"
          max-width-calc
          :hide-details="2"
        ></ui-select>

        <template v-if="isFromPrivateToPublic && !conflict.usedItems.length">
          <p>
            {{ t('You are moving the element from private workspace to Public.') }}
          </p>
          {{ t('What changes:') }}
          <ul class="mb-8">
            <li>
              {{ t('this element will be visible in the settings of other elements in all the workspaces;') }}
            </li>
            <li>
              {{ t('this element will be visible for all your team members, if you work with the multi-user access.') }}
            </li>
          </ul>
        </template>

        <template v-if="isFromPublicToPrivate && !conflict.usedItems.length">
          <p>
            {{ t('You are moving the element from private workspace to Public.') }}
          </p>
          {{ t('What changes:') }}
          <ul class="mb-8">
            <li>
              {{ t('this element will be visible in the settings of other elements in all the workspaces;') }}
            </li>
            <li>
              {{ t('this element will be visible for all your team members, if you work with the multi-user access.') }}
            </li>
          </ul>
        </template>

        <template v-if="isFromPrivateToPrivate && !conflict.usedItems.length">
          <p>
            {{ t('You are moving the element between private workspaces.') }}
          </p>
          {{ t('What changes:') }}
          <ul class="mb-8">
            <li>
              {{ t('this element will be visible only in the settings of the elements from the workspace to which you’re moving it;') }}
            </li>
            <li>
              {{ t('this element will no longer be visible to the team members who have no access to the workspace to which you’re moving the element.') }}
            </li>
          </ul>
        </template>

        <template v-if="conflict.usedItems.length">
          <ui-info color="red" class="mb-8">
            {{ t('Moving this element to other workspace is not possible.') }}
          </ui-info>
          <p>
            {{ t('This element has connections with other elements:') }}
          </p>
          <div class="conflict-list mb-8">
            <div v-for="(item, index) in conflict.usedItems" :key="index">
              <template v-if="item.available === false">
                {{ t('{0} is used by element which is not available in your workspaces.', [item.usedName]) }}
              </template>
              <template v-else>
                {{ t('{0} is used by {1}', [item.usedName, item.usingName]) }}
                ({{ getUsingType({ type: item.usingType }) }})
              </template>
            </div>
            <div v-if="conflict.allUsages > 20">...</div>
          </div>
          {{ t('What are the options:') }}
          <ol class="mb-8">
            <li>
              {{ t('remove the element from the settings of the elements specified in the list above;') }}
            </li>
            <li>
              {{ t('after that move the element to the required workspace.') }}
            </li>
          </ol>
          <p>
            {{ t('Close this window and move to the settings of the elements.') }}
          </p>
        </template>

        <ui-info>
          <span v-html="t('The detailed guide on moving elements between workspaces is available in our {0}.', [`<a href='https://docs.bemob.com/${locale}/moving-elements-between-workspaces' target='_blank'>Help Desk</a>`])"></span>
          <br />
          {{ t('If any assistance is required - contact the support team via Live Chat.') }}
        </ui-info>
      </div>
    </template>
    <template #footer>
      <section class="justify-space-between" style="display: flex;">
        <div>
          <error-aggregator v-model="errors" v-model:active="errorsControlActive"></error-aggregator>
        </div>
        <div class="d-flex justify-end">
          <ui-button
            icon="fas fa-times"
            :text="t('Cancel')"
            @click="close"
          ></ui-button>
          <ui-button
            button-style="accent"
            icon="fas fa-check"
            :text="t('Move Element')"
            @click="submit"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useApiFieldErrors, useGetUsingType, useI18n } from '@/composables';
import { patchEntityChangeWorkspaceApiRequest } from '@/services/api/entity-api';
import { $emitter } from '@/services/emitter';
import { useStore } from '@/store';

type Workspace = {
  type?: 'private' | 'public';
  name?: string;
  id?: string;
};

type ConflictItem = {
  available?: boolean;
  usedName?: string;
  usingName?: string;
  usingType?: string;
};

type ConflictState = {
  usedItems: ConflictItem[];
  allUsages: number | null;
};

const emit = defineEmits<{
  close: [];
}>();

const { t, locale } = useI18n();
const route = useRoute();
const store = useStore();
const { getUsingType } = useGetUsingType();
const { errors, errorsControlActive, errorsDict, fieldErrors, clearErrors } =
  useApiFieldErrors();

const active = ref(false);
const selectedWorkspace = ref<Workspace>({});

const defaultConflictState = (): ConflictState => ({
  usedItems: [],
  allUsages: null,
});

const conflict = ref<ConflictState>(defaultConflictState());

const selectedItem = computed(() => store.getTableSelectedItem());

const selectedItemWorkspaceType = computed(
  () =>
    store.user.workspaces.find((item) => item.id === selectedItem.value.id)?.type,
);

const workspacesList = computed(() => {
  if (route.params.type === 'campaigns') {
    return store.user.workspaces.filter(
      (item) => item.type !== 'public' && item.id !== selectedItem.value.workspaceId,
    );
  }

  return store.user.workspaces.filter(
    (item) => item.id !== selectedItem.value.workspaceId,
  );
});

const isFromPrivateToPublic = computed(
  () =>
    selectedWorkspace.value.type === 'private' &&
    selectedItemWorkspaceType.value === 'public',
);

const isFromPublicToPrivate = computed(
  () =>
    selectedWorkspace.value.type === 'public' &&
    selectedItemWorkspaceType.value === 'private',
);

const isFromPrivateToPrivate = computed(
  () =>
    selectedWorkspace.value.type === 'private' &&
    selectedItemWorkspaceType.value === 'private',
);

const resetErrorAggregator = () => {
  clearErrors();
};

const close = () => {
  emit('close');
};

const submit = async () => {
  if (!selectedWorkspace.value.id) {
    const message = t('Workspace should be selected');
    fieldErrors.value = { selectedWorkspace: [message] };
    errors.value = [{ message: t('Validation failed') }, { message }];
    errorsControlActive.value = true;
    return;
  }

  const elementId = selectedItem.value?.id;

  try {
    await patchEntityChangeWorkspaceApiRequest(
      route.params.type as string,
      elementId,
      selectedWorkspace.value.id,
    );
    $emitter.emit('refresh-report');
    close();
    $emitter.emit('toast-success', t('Workspace changed successfully'));
  } catch (error) {
    const responseData = (error as { response?: { data?: { message?: string; payload?: ConflictState } } })
      ?.response?.data;

    if (responseData?.message === 'WORKSPACE_CONFLICT' && responseData.payload) {
      conflict.value = {
        usedItems: responseData.payload.usedItems || [],
        allUsages: responseData.payload.allUsages,
      };
      errors.value = [
        { message: t('Moving this element to other workspace is not possible.') },
      ];
      errorsControlActive.value = true;
    }
  }
};

watch(selectedWorkspace, () => {
  resetErrorAggregator();
  conflict.value = defaultConflictState();
});

onMounted(() => {
  active.value = true;
});
</script>

<style lang="scss" scoped>
.conflict-list {
  max-height: 150px;
  overflow-y: auto;
  padding: 8px 8px 0;
  border-radius: 8px;
  background-color: var(--color-gray5);
  div {
    margin-bottom: 8px;
    line-height: 1;
  }
}
</style>
